import is from '@sindresorhus/is';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { UseFormGetValues, UseFormRegister, UseFormReset, UseFormWatch } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import type { JobArchitectureMetadata } from '~/shared/data-contract/job-architecture';

import type { CompanyInformationGenerationResult } from '../../data-contract/company-information-generation';

export const COMPANY_SIZE_MAX = 10_000_000;

export type CompanyInformationFormValues = {
    readonly companyDescription: string;
    readonly companyDomain: string;
    readonly companyName: string;
    readonly companyPriorities: string;
    readonly companySize: string;
};

export type MetadataHydrationValues = Omit<CompanyInformationFormValues, 'companyDomain'>;

const emptyValues: CompanyInformationFormValues = {
    companyDescription: '',
    companyDomain: '',
    companyName: '',
    companyPriorities: '',
    companySize: '',
};

export const isCompanySizeInRange = (value: string): boolean => {
    if (is.emptyStringOrWhitespace(value)) return false;
    const size = Number.parseInt(value, 10);
    return !Number.isNaN(size) && size >= 1 && size <= COMPANY_SIZE_MAX;
};

export const areMetadataFieldsComplete = (values: MetadataHydrationValues): boolean =>
    !is.emptyStringOrWhitespace(values.companyName) &&
    !is.emptyStringOrWhitespace(values.companyDescription) &&
    !is.emptyStringOrWhitespace(values.companyPriorities) &&
    isCompanySizeInRange(values.companySize);

export const deriveFromMetadata = (metadata: JobArchitectureMetadata): MetadataHydrationValues => ({
    companyDescription: metadata.companyDescription ?? '',
    companyName: metadata.companyName ?? '',
    companyPriorities: metadata.companyPriorities ?? '',
    companySize: is.number(metadata.companySize) && metadata.companySize > 0 ? String(metadata.companySize) : '',
});

export type CompanyInformationFormHandle = {
    readonly confirmName: (companyName: string, companyDomain: string) => void;
    readonly consumeRegenerateIntent: () => boolean;
    readonly getValues: UseFormGetValues<CompanyInformationFormValues>;
    readonly hadGenerationError: boolean;
    readonly hydrateDomainIfEmpty: (domain: string) => void;
    readonly isAwaitingGeneration: boolean;
    readonly isHydratedFromCompleteMetadata: boolean;
    readonly isNameConfirmed: boolean;
    readonly markForRegenerate: () => void;
    readonly observeError: () => void;
    readonly populateResult: (result: CompanyInformationGenerationResult) => void;
    readonly register: UseFormRegister<CompanyInformationFormValues>;
    readonly reset: UseFormReset<CompanyInformationFormValues>;
    readonly rewrite: () => void;
    readonly submittedCompanyDomain: string | null;
    readonly submittedCompanyName: string | null;
    readonly watch: UseFormWatch<CompanyInformationFormValues>;
};

export const useCompanyInformationForm = (
    jobArchitectureId: string | undefined,
    metadata: JobArchitectureMetadata | null | undefined,
): CompanyInformationFormHandle => {
    const { getValues, register, reset, setValue, watch } = useForm<CompanyInformationFormValues>({
        defaultValues: emptyValues,
        mode: 'onChange',
    });

    const [isNameConfirmed, setIsNameConfirmed] = useState(false);
    const [isAwaitingGeneration, setIsAwaitingGeneration] = useState(false);
    const [hadGenerationError, setHadGenerationError] = useState(false);
    const [isHydratedFromCompleteMetadata, setIsHydratedFromCompleteMetadata] = useState(false);
    const [submittedCompanyName, setSubmittedCompanyName] = useState<string | null>(null);
    const [submittedCompanyDomain, setSubmittedCompanyDomain] = useState<string | null>(null);
    const shouldRegenerateRef = useRef(false);
    const hydratedIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (is.undefined(jobArchitectureId) || is.nullOrUndefined(metadata)) return;
        if (hydratedIdRef.current === jobArchitectureId) return;
        hydratedIdRef.current = jobArchitectureId;

        const derived = deriveFromMetadata(metadata);
        reset({ ...derived, companyDomain: '' });
        if (areMetadataFieldsComplete(derived)) {
            setIsNameConfirmed(true);
            setIsHydratedFromCompleteMetadata(true);
        }
    }, [jobArchitectureId, metadata, reset]);

    const clearGeneratedFields = useCallback((): void => {
        setValue('companyDescription', '', { shouldValidate: true });
        setValue('companyPriorities', '', { shouldValidate: true });
        setValue('companySize', '', { shouldValidate: true });
    }, [setValue]);

    const confirmName = useCallback(
        (companyName: string, companyDomain: string): void => {
            clearGeneratedFields();
            setIsNameConfirmed(true);
            setIsAwaitingGeneration(true);
            setIsHydratedFromCompleteMetadata(false);
            setHadGenerationError(false);
            setSubmittedCompanyName(companyName);
            setSubmittedCompanyDomain(companyDomain);
        },
        [clearGeneratedFields],
    );

    const rewrite = useCallback((): void => {
        clearGeneratedFields();
        setValue('companyDomain', '');
        setIsNameConfirmed(false);
        setIsAwaitingGeneration(false);
        setIsHydratedFromCompleteMetadata(false);
        setHadGenerationError(false);
        setSubmittedCompanyName(null);
        setSubmittedCompanyDomain(null);
    }, [clearGeneratedFields, setValue]);

    const populateResult = useCallback(
        (result: CompanyInformationGenerationResult): void => {
            setValue('companyDescription', result.companyDescription, { shouldValidate: true });
            setValue('companyPriorities', result.companyPriorities, { shouldValidate: true });
            setValue('companySize', String(result.companySize), { shouldValidate: true });
            setIsAwaitingGeneration(false);
            setHadGenerationError(false);
        },
        [setValue],
    );

    const observeError = useCallback((): void => {
        setIsAwaitingGeneration(false);
        setHadGenerationError(true);
    }, []);

    const hydrateDomainIfEmpty = useCallback(
        (domain: string): void => {
            if (is.emptyStringOrWhitespace(getValues('companyDomain'))) {
                setValue('companyDomain', domain);
            }
        },
        [getValues, setValue],
    );

    const consumeRegenerateIntent = useCallback((): boolean => {
        const value = shouldRegenerateRef.current;
        shouldRegenerateRef.current = false;
        return value;
    }, []);

    const markForRegenerate = useCallback((): void => {
        shouldRegenerateRef.current = true;
    }, []);

    return {
        confirmName,
        consumeRegenerateIntent,
        getValues,
        hadGenerationError,
        hydrateDomainIfEmpty,
        isAwaitingGeneration,
        isHydratedFromCompleteMetadata,
        isNameConfirmed,
        markForRegenerate,
        observeError,
        populateResult,
        register,
        reset,
        rewrite,
        submittedCompanyDomain,
        submittedCompanyName,
        watch,
    };
};
